import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/layout/Layout';
import { Loader2, ExternalLink, LayoutDashboard, Users, CalendarDays, MessageSquare, Target, Star, CreditCard, Plus, Trash2, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CoachDashboard() {
  const [tab, setTab] = useState('overview');
  const [access, setAccess] = useState(null);
  useEffect(() => { axios.get(`${API}/api/coach/me`, { withCredentials: true }).then(() => setAccess(true)).catch(() => setAccess(false)); }, []);

  if (access === null) return <Layout><div className="max-w-3xl mx-auto p-8 text-center text-[#94A3B8]">Loading…</div></Layout>;
  if (access === false) return <Layout><div className="max-w-3xl mx-auto p-12 text-center"><h1 className="text-2xl font-bold">Coach access required</h1><p className="text-[#475569] mt-2">This area is for approved DCA coaches.</p></div></Layout>;

  const TABS = [
    ['overview', 'Overview', LayoutDashboard], ['clients', 'Clients', Users], ['calendar', 'Availability', CalendarDays],
    ['messages', 'Messages', MessageSquare], ['goals', 'Goals', Target], ['reviews', 'Reviews', Star], ['payouts', 'Payouts', CreditCard],
  ];
  return (
    <Layout title="Coach Dashboard">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-bold text-[#0A0F1C] mb-5">Coach Workspace</h1>
        <div className="flex gap-1 border-b border-[#E2E8F0] mb-6 overflow-x-auto">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} className={`px-3 py-2 text-sm font-bold rounded-t whitespace-nowrap ${tab === id ? 'bg-[#1D4ED8] text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}`} data-testid={`cw-tab-${id}`}><Icon size={14} className="inline mr-1.5 -mt-0.5" />{label}</button>
          ))}
        </div>
        {tab === 'overview' && <Overview />}
        {tab === 'clients' && <Clients />}
        {tab === 'calendar' && <Availability />}
        {tab === 'messages' && <Messages />}
        {tab === 'goals' && <Goals />}
        {tab === 'reviews' && <Reviews />}
        {tab === 'payouts' && <Payouts />}
      </div>
    </Layout>
  );
}

function Stat({ label, value }) { return <div className="bg-white border border-[#E2E8F0] rounded-xl p-4"><div className="text-xs uppercase text-[#94A3B8]">{label}</div><div className="text-2xl font-bold text-[#0A0F1C]">{value}</div></div>; }

function Overview() {
  const [a, setA] = useState(null);
  const [me, setMe] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/coach/analytics`, { withCredentials: true }).then(r => setA(r.data));
    axios.get(`${API}/api/coach/me`, { withCredentials: true }).then(r => setMe(r.data));
  }, []);
  if (!a) return <div className="text-center text-[#94A3B8] py-8">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="cw-overview">
      {me && <div className="flex items-center justify-between bg-white border border-[#E2E8F0] rounded-xl p-4"><div><div className="font-bold">{me.name}</div><div className="text-xs text-[#475569]">{me.headline || 'Add a headline in Admin/your profile'}</div></div><a href={`/coaches/${me.slug}`} target="_blank" rel="noreferrer" className="btn-outline text-sm inline-flex items-center gap-1" data-testid="cw-view-public"><ExternalLink size={13} />Public profile</a></div>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Clients" value={a.clients_total} />
        <Stat label="Active clients" value={a.clients_by_status.active} />
        <Stat label="Upcoming bookings" value={a.bookings_upcoming} />
        <Stat label="Avg rating" value={a.avg_rating ?? '—'} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total bookings" value={a.bookings_total} />
        <Stat label="Goals active" value={a.goals_active} />
        <Stat label="Goals done" value={a.goals_completed} />
        <Stat label="Reviews" value={a.reviews_count} />
      </div>
    </div>
  );
}

function Clients() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const load = useCallback(() => axios.get(`${API}/api/coach/clients`, { withCredentials: true }).then(r => setRows(r.data || [])), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!email) return; await axios.post(`${API}/api/coach/clients`, { client_email: email, client_name: name }, { withCredentials: true }); setEmail(''); setName(''); load(); };
  const setStatus = async (ce, status) => { await axios.patch(`${API}/api/coach/clients/${ce}`, { status }, { withCredentials: true }); load(); };
  return (
    <div className="space-y-3" data-testid="cw-clients">
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 grid md:grid-cols-3 gap-2">
        <input className="px-3 py-2 border rounded text-sm" placeholder="Client email" value={email} onChange={e => setEmail(e.target.value)} data-testid="client-email" />
        <input className="px-3 py-2 border rounded text-sm" placeholder="Client name" value={name} onChange={e => setName(e.target.value)} />
        <button onClick={add} className="btn-primary-orange text-sm" data-testid="client-add"><Plus size={13} className="inline" />Add client</button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-xl divide-y">
        {rows.map(c => (
          <div key={c.id} className="p-3" data-testid={`client-row-${c.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0"><div className="font-semibold text-sm">{c.client_name}</div><div className="text-xs text-[#94A3B8]">{c.client_email} · {c.notes_count} notes</div></div>
              <select value={c.status} onChange={e => setStatus(c.client_email, e.target.value)} className="text-xs border rounded px-2 py-1" data-testid={`client-status-${c.id}`}><option value="lead">Lead</option><option value="active">Active</option><option value="past">Past</option></select>
              <button onClick={() => setOpen(open === c.client_email ? null : c.client_email)} className="btn-outline text-xs">{open === c.client_email ? 'Close' : 'Notes'}</button>
            </div>
            {open === c.client_email && <ClientNotes clientEmail={c.client_email} />}
          </div>
        ))}
        {rows.length === 0 && <div className="p-8 text-center text-sm text-[#94A3B8]">No clients yet. Add one or have an admin assign clients to you.</div>}
      </div>
    </div>
  );
}

function ClientNotes({ clientEmail }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const load = useCallback(() => axios.get(`${API}/api/coach/clients/${clientEmail}/notes`, { withCredentials: true }).then(r => setNotes(r.data || [])), [clientEmail]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!text.trim()) return; await axios.post(`${API}/api/coach/clients/${clientEmail}/notes`, { body: text }, { withCredentials: true }); setText(''); load(); };
  return (
    <div className="mt-3 bg-[#F8FAFC] rounded p-3 space-y-2">
      <div className="flex gap-2"><input className="flex-1 px-2 py-1.5 border rounded text-sm" placeholder="Add a private note…" value={text} onChange={e => setText(e.target.value)} data-testid="note-input" /><button onClick={add} className="btn-primary-orange text-xs">Add</button></div>
      {notes.map(n => <div key={n.note_id} className="text-xs bg-white rounded p-2 border">{n.body}<div className="text-[10px] text-[#94A3B8] mt-1">{new Date(n.created_at).toLocaleString()}</div></div>)}
    </div>
  );
}

function Availability() {
  const [av, setAv] = useState(null);
  useEffect(() => { axios.get(`${API}/api/coach/availability`, { withCredentials: true }).then(r => setAv(r.data)); }, []);
  if (!av) return <div className="text-center text-[#94A3B8] py-8">Loading…</div>;
  const addRow = () => setAv({ ...av, weekly: [...av.weekly, { day_of_week: 1, start: '09:00', end: '10:00' }] });
  const upd = (i, p) => { const w = [...av.weekly]; w[i] = { ...w[i], ...p }; setAv({ ...av, weekly: w }); };
  const save = async () => { await axios.put(`${API}/api/coach/availability`, av, { withCredentials: true }); toast.success('Availability saved'); };
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3" data-testid="cw-availability">
      <div className="flex gap-3 items-end">
        <label className="text-xs text-[#475569] flex flex-col gap-1">Time zone<input className="px-3 py-2 border rounded text-sm" value={av.timezone} onChange={e => setAv({ ...av, timezone: e.target.value })} /></label>
        <label className="text-xs text-[#475569] flex flex-col gap-1">Slot (min)<input type="number" className="px-3 py-2 border rounded text-sm w-24" value={av.slot_minutes} onChange={e => setAv({ ...av, slot_minutes: parseInt(e.target.value) || 30 })} /></label>
      </div>
      <div className="space-y-2">
        {av.weekly.map((w, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`avail-row-${i}`}>
            <select value={w.day_of_week} onChange={e => upd(i, { day_of_week: parseInt(e.target.value) })} className="border rounded px-2 py-1.5 text-sm">{DAYS.map((d, idx) => <option key={idx} value={idx}>{d}</option>)}</select>
            <input type="time" value={w.start} onChange={e => upd(i, { start: e.target.value })} className="border rounded px-2 py-1.5 text-sm" />
            <span>–</span>
            <input type="time" value={w.end} onChange={e => upd(i, { end: e.target.value })} className="border rounded px-2 py-1.5 text-sm" />
            <button onClick={() => setAv({ ...av, weekly: av.weekly.filter((_, j) => j !== i) })} className="text-red-600"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2"><button onClick={addRow} className="btn-outline text-sm"><Plus size={13} className="inline" />Add window</button><button onClick={save} className="btn-primary-orange text-sm" data-testid="avail-save">Save availability</button></div>
    </div>
  );
}

function Messages() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const loadThreads = useCallback(() => axios.get(`${API}/api/coach/messages/threads`, { withCredentials: true }).then(r => setThreads(r.data || [])), []);
  useEffect(() => { loadThreads(); }, [loadThreads]);
  const openThread = async (ce) => { setActive(ce); const r = await axios.get(`${API}/api/coach/messages/${ce}`, { withCredentials: true }); setMsgs(r.data || []); loadThreads(); };
  const send = async () => { if (!text.trim()) return; await axios.post(`${API}/api/coach/messages/${active}`, { body: text }, { withCredentials: true }); setText(''); openThread(active); };
  return (
    <div className="grid md:grid-cols-3 gap-3" data-testid="cw-messages">
      <div className="bg-white border border-[#E2E8F0] rounded-xl divide-y md:col-span-1">
        {threads.map(t => <button key={t.client_email} onClick={() => openThread(t.client_email)} className={`w-full text-left p-3 ${active === t.client_email ? 'bg-[#EFF6FF]' : ''}`} data-testid={`thread-${t.client_email}`}><div className="text-sm font-semibold flex justify-between">{t.client_email}{t.unread > 0 && <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5">{t.unread}</span>}</div><div className="text-xs text-[#94A3B8] truncate">{t.last}</div></button>)}
        {threads.length === 0 && <div className="p-6 text-center text-sm text-[#94A3B8]">No conversations yet.</div>}
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-xl md:col-span-2 flex flex-col min-h-[300px]">
        {!active ? <div className="flex-1 flex items-center justify-center text-sm text-[#94A3B8]">Select a conversation</div> : (
          <>
            <div className="flex-1 p-3 space-y-2 overflow-y-auto max-h-[360px]">{msgs.map(m => <div key={m.message_id} className={`text-sm max-w-[80%] rounded-lg px-3 py-2 ${m.sender === 'coach' ? 'bg-[#1D4ED8] text-white ml-auto' : 'bg-[#F1F5F9]'}`}>{m.body}</div>)}</div>
            <div className="p-3 border-t flex gap-2"><input className="flex-1 px-3 py-2 border rounded text-sm" placeholder="Message…" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === 'Enter' && send()} data-testid="msg-input" /><button onClick={send} className="btn-primary-orange text-sm px-3" data-testid="msg-send"><Send size={14} /></button></div>
          </>
        )}
      </div>
    </div>
  );
}

function Goals() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ client_email: '', title: '', target_date: '' });
  const load = useCallback(() => axios.get(`${API}/api/coach/goals`, { withCredentials: true }).then(r => setRows(r.data || [])), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => { if (!f.title || !f.client_email) return toast.error('Title + client email required'); await axios.post(`${API}/api/coach/goals`, f, { withCredentials: true }); setF({ client_email: '', title: '', target_date: '' }); load(); };
  const toggle = async (g) => { await axios.patch(`${API}/api/coach/goals/${g.goal_id}`, { status: g.status === 'done' ? 'active' : 'done' }, { withCredentials: true }); load(); };
  return (
    <div className="space-y-3" data-testid="cw-goals">
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 grid md:grid-cols-4 gap-2">
        <input className="px-3 py-2 border rounded text-sm" placeholder="Client email" value={f.client_email} onChange={e => setF({ ...f, client_email: e.target.value })} data-testid="goal-client" />
        <input className="px-3 py-2 border rounded text-sm md:col-span-2" placeholder="Goal title" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="goal-title" />
        <button onClick={create} className="btn-primary-orange text-sm" data-testid="goal-create">Add goal</button>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-xl divide-y">
        {rows.map(g => (
          <div key={g.goal_id} className="p-3 flex items-center gap-3" data-testid={`goal-row-${g.goal_id}`}>
            <input type="checkbox" checked={g.status === 'done'} onChange={() => toggle(g)} data-testid={`goal-check-${g.goal_id}`} />
            <div className="flex-1"><div className={`text-sm font-semibold ${g.status === 'done' ? 'line-through text-[#94A3B8]' : ''}`}>{g.title}</div><div className="text-xs text-[#94A3B8]">{g.client_email}{g.target_date ? ` · due ${g.target_date}` : ''}</div></div>
            <button onClick={async () => { await axios.delete(`${API}/api/coach/goals/${g.goal_id}`, { withCredentials: true }); load(); }} className="text-red-600"><Trash2 size={14} /></button>
          </div>
        ))}
        {rows.length === 0 && <div className="p-8 text-center text-sm text-[#94A3B8]">No goals yet.</div>}
      </div>
    </div>
  );
}

function Reviews() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => axios.get(`${API}/api/coach/reviews`, { withCredentials: true }).then(r => setRows(r.data || [])), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => { await axios.patch(`${API}/api/coach/reviews/${id}`, { status }, { withCredentials: true }); load(); };
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl divide-y" data-testid="cw-reviews">
      {rows.map(r => (
        <div key={r.review_id} className="p-3" data-testid={`review-row-${r.review_id}`}>
          <div className="flex items-center gap-2"><span className="text-[#F97316]">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span><span className="font-semibold text-sm">{r.author_name}</span><span className={`text-[10px] uppercase px-2 py-0.5 rounded ml-auto ${r.status === 'published' ? 'bg-green-100 text-green-700' : r.status === 'hidden' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span></div>
          <div className="text-sm text-[#475569] mt-1">{r.text}</div>
          <div className="flex gap-1.5 mt-2">{r.status !== 'published' && <button onClick={() => setStatus(r.review_id, 'published')} className="btn-outline text-xs">Publish</button>}{r.status !== 'hidden' && <button onClick={() => setStatus(r.review_id, 'hidden')} className="btn-outline text-xs">Hide</button>}</div>
        </div>
      ))}
      {rows.length === 0 && <div className="p-8 text-center text-sm text-[#94A3B8]">No reviews yet.</div>}
    </div>
  );
}

function Payouts() {
  const [status, setStatus] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/coach/connect/status`, { withCredentials: true }).then(r => setStatus(r.data)).catch(() => setStatus({ connected: false }));
    axios.get(`${API}/api/coach/connect/payouts`, { withCredentials: true }).then(r => setPayouts(r.data)).catch(() => setPayouts(null));
  }, []);
  const onboard = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/api/coach/connect/onboard`, { origin_url: window.location.origin }, { withCredentials: true }); window.location.href = r.data.url; }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); setBusy(false); }
  };
  if (!status) return <div className="text-center text-[#94A3B8] py-8">Loading…</div>;

  const fmt = (n, c = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency: c }).format(n || 0);
  const fmtDate = (ts) => ts ? new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const ready = status.charges_enabled || status.ready;
  const cur = payouts?.currency || 'USD';

  // Onboarding progress: 3 simple milestones
  const steps = [
    { label: 'Stripe account connected', done: !!status.connected },
    { label: 'Onboarding complete', done: !!status.onboarding_complete },
    { label: 'Ready to accept payments', done: !!ready },
  ];
  const pct = Math.round((steps.filter(s => s.done).length / steps.length) * 100);

  return (
    <div className="space-y-4" data-testid="cw-payouts">
      {/* Status + onboarding progress */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-bold">Get paid for your sessions</h2>
            <p className="text-sm text-[#475569] max-w-md">Connect a Stripe account to accept payments. DCA takes a small platform fee; the rest is paid out to you automatically.</p>
          </div>
          <div className="flex items-center gap-2 text-sm whitespace-nowrap">
            <span className={`w-2.5 h-2.5 rounded-full ${ready ? 'bg-green-500' : status.connected ? 'bg-amber-400' : 'bg-slate-300'}`} />
            {ready ? 'Ready to accept payments' : status.connected ? 'Finish onboarding' : 'Not connected'}
          </div>
        </div>

        {/* Progress bar */}
        <div>
          <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden" data-testid="payout-progress">
            <div className="h-full bg-[#1D4ED8] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="grid sm:grid-cols-3 gap-2 mt-3">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs" data-testid={`payout-step-${i}`}>
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${s.done ? 'bg-green-500 text-white' : 'bg-[#E2E8F0] text-[#94A3B8]'}`}>{s.done ? '✓' : i + 1}</span>
                <span className={s.done ? 'text-[#0A0F1C]' : 'text-[#94A3B8]'}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {!ready && (
          <button onClick={onboard} disabled={busy} className="btn-primary-orange text-sm" data-testid="connect-onboard">
            {busy ? <Loader2 size={14} className="animate-spin inline" /> : status.connected ? 'Continue onboarding' : 'Connect Stripe'}
          </button>
        )}
      </div>

      {/* Balance cards */}
      {status.connected && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="payout-available">
            <div className="text-xs uppercase text-[#94A3B8]">Available balance</div>
            <div className="text-3xl font-bold text-[#0A0F1C] mt-1">{payouts ? fmt(payouts.available, cur) : '…'}</div>
            <div className="text-xs text-[#94A3B8] mt-1">Ready to pay out</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="payout-pending">
            <div className="text-xs uppercase text-[#94A3B8]">Pending balance</div>
            <div className="text-3xl font-bold text-[#0A0F1C] mt-1">{payouts ? fmt(payouts.pending, cur) : '…'}</div>
            <div className="text-xs text-[#94A3B8] mt-1">Clearing from recent sales</div>
          </div>
        </div>
      )}

      {/* Recent payouts */}
      {status.connected && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="payout-history">
          <h3 className="font-bold text-[#0A0F1C] mb-3">Recent payouts</h3>
          {payouts?.error && <p className="text-sm text-amber-600">{payouts.error}</p>}
          {payouts && !payouts.error && payouts.payouts.length === 0 && (
            <p className="text-sm text-[#94A3B8]">No payouts yet. They'll appear here once you start earning and Stripe sends your first transfer.</p>
          )}
          {payouts && payouts.payouts.length > 0 && (
            <div className="divide-y">
              {payouts.payouts.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 text-sm" data-testid={`payout-row-${i}`}>
                  <div>
                    <div className="font-bold text-[#0A0F1C]">{fmt(p.amount, p.currency)}</div>
                    <div className="text-xs text-[#94A3B8]">Arrives {fmtDate(p.arrival_date)}</div>
                  </div>
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full font-bold ${p.status === 'paid' ? 'bg-green-100 text-green-700' : p.status === 'in_transit' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{(p.status || '').replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
